/**
 * Store shell, Operations → Serial numbers (flag `serial_capture`, MOT-357).
 *
 * One page, two jobs the store does at the counter and at month end: look a
 * serial up for a warranty question (order, product, sale date; never a
 * customer), and keep the shadow crosswalk of serials on hand per SKU, which
 * the rebate claim export reads. Received serials come in by CSV or one at a
 * time; sold ones arrive from Shopify line properties and Lightspeed
 * serialized sales, or are typed onto a line on the Rebate Report.
 *
 * Slice 2 (MOT-374): the same serial under two brands is two units, so the
 * lookup lists every unit with the serial and a unit with no catalog product
 * shows its brand; a sold line shows the rebate claim state of the serial.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { SearchOutlined, UploadFileOutlined, AddOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useOrganizations } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { SERIAL_RECEIVING_TEMPLATE } from '../constants/csvTemplates';
import {
  BrandSelect,
  CsvTemplateHint,
  DataTableShell,
  EmptyState,
  FormDialog,
  InlineMessage,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
  TableSkeleton,
} from '../components/ui';
import type { StatusTone } from '../components/ui';
import { tokens } from '../theme/tokens';
import { TrialFeatureTitle } from '../components/trial/TrialFeatureBadge';

type UnitStatus = 'in_stock' | 'sold' | 'returned';
type StatusFilter = 'all' | UnitStatus;

interface UnitProduct {
  id: string;
  name: string;
  brand: string | null;
  sku: string | null;
}
interface SerialUnit {
  id: string;
  serial: string;
  brand?: string | null;
  productId: string | null;
  storeSku: string | null;
  status: UnitStatus;
  source: 'receiving_csv' | 'manual' | 'sale';
  receivedAt: string | null;
  soldOrderItemId: string | null;
  soldAt: string | null;
  product: UnitProduct | null;
}
interface SerialList {
  units: SerialUnit[];
  /** Null for a search, which is bounded by the page size and never counted. */
  total: number | null;
  counts: Record<UnitStatus, number>;
  nextCursor: string | null;
}
interface SoldLine {
  orderItemId: string;
  orderNumber: string;
  orderedAt: string | null;
  platform: string | null;
  sku: string;
  productName: string;
  quantity: number;
  canonicalProductId: string | null;
  serialNumbers: string[];
  /** One entry per (rebate program, serial) claimed on this line (MOT-420). */
  serialClaims?: Array<{ programKey: string; serial: string; status: 'submitted' | 'paid'; reference: string | null }>;
  /** Claims that kept this refunded line's unit sold (MOT-419). */
  serialRefundHolds?: Array<{ programKey: string; serial: string; status: 'submitted' | 'paid' }>;
  financialStatus: string;
}
interface Lookup {
  serial: string;
  unit: SerialUnit | null;
  /** Every unit with the serial, one per brand (slice 2). */
  units?: SerialUnit[];
  soldLines: SoldLine[];
}
interface ReceiveResult {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  unmatched: number;
  errors: Array<{ row: number; message: string }>;
}

const PAGE_SIZE = 50;
const WRITE_ROLES = new Set(['owner', 'admin', 'manager']);
const STATUS_TONE: Record<UnitStatus, StatusTone> = { in_stock: 'info', sold: 'positive', returned: 'caution' };

const productLink = (id: string): string => `/app/products?productId=${encodeURIComponent(id)}`;

const readError = async (res: Response, fallback: string): Promise<string> => {
  const body = await res.json().catch(() => ({}));
  return body?.error?.message || body?.error || body?.message || fallback;
};

const StoreSerialsPage: React.FC = () => {
  const { t } = useAppTranslation();
  const { formatDate } = useLocale();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { isEnabled, isLoading: flagsLoading } = useOrgFeatureFlags();
  const enabled = isEnabled('serial_capture');
  const { actingOrganization } = useOrganizations();
  const { isAdmin } = useAuth();
  // Writes are owner / admin / manager on the server; a platform admin acting as the store passes too.
  const canWrite = isAdmin || (actingOrganization ? WRITE_ROLES.has(actingOrganization.role) : false);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  /** Pages fetched so far: the first page plus one per "Load more"; cleared when a filter changes. */
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.length > 0 ? cursors[cursors.length - 1] : undefined;
  const resetPages = () => setCursors([]);
  const [lookupInput, setLookupInput] = useState('');
  const [lookupSerial, setLookupSerial] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<ReceiveResult | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addSerial, setAddSerial] = useState('');
  const [addSku, setAddSku] = useState('');
  const [addBrand, setAddBrand] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);

  const day = (iso: string | null | undefined): string => (iso ? formatDate(iso) : '—');
  const statusLabel = (s: UnitStatus) => t(`serialsPage.status.${s}`);
  const sourceLabel = (s: SerialUnit['source']) => t(`serialsPage.source.${s}`);

  const listKey = ['store-serials', statusFilter, search, cursor ?? ''];
  const { data, isLoading, error } = useQuery<SerialList>({
    queryKey: listKey,
    enabled,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (cursor) params.set('cursor', cursor);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/serials?${params.toString()}`, { headers });
      if (!res.ok) throw new Error(await readError(res, t('serialsPage.loadError')));
      return (await res.json()).data as SerialList;
    },
  });

  const lookup = useQuery<Lookup>({
    queryKey: ['store-serials-lookup', lookupSerial],
    enabled: enabled && lookupSerial.length > 0,
    retry: false,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/serials/lookup?serial=${encodeURIComponent(lookupSerial)}`, { headers });
      if (!res.ok) throw new Error(await readError(res, t('serialsPage.lookup.error')));
      return (await res.json()).data as Lookup;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['store-serials'] });
    queryClient.invalidateQueries({ queryKey: ['store-serials-lookup'] });
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const headers = (await getAuthHeaders()) as Record<string, string>;
      delete headers['Content-Type'];
      const body = new FormData();
      body.append('file', file);
      const res = await makeApiCall('/store/serials/receive', { method: 'POST', headers, body });
      if (!res.ok) throw new Error(await readError(res, t('serialsPage.upload.error')));
      return (await res.json()).data as ReceiveResult;
    },
    onSuccess: (result) => {
      setUploadResult(result);
      setUploadFile(null);
      invalidate();
      showToast(t('serialsPage.upload.done', { created: result.created, updated: result.updated, skipped: result.skipped }), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/store/serials', {
        method: 'POST',
        headers,
        body: JSON.stringify({ serial: addSerial.trim(), storeSku: addSku.trim() || null, brand: addBrand.trim() || null }),
      });
      if (!res.ok) throw new Error(await readError(res, t('serialsPage.add.error')));
      return (await res.json()).data as ReceiveResult;
    },
    onSuccess: (result) => {
      setAddOpen(false);
      setAddSerial('');
      setAddSku('');
      setAddBrand('');
      invalidate();
      showToast(result.unmatched > 0 ? t('serialsPage.add.doneUnmatched') : t('serialsPage.add.done'), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: UnitStatus }) => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/serials/${encodeURIComponent(id)}`, { method: 'PATCH', headers, body: JSON.stringify({ status }) });
      if (!res.ok) throw new Error(await readError(res, t('serialsPage.statusError')));
      return (await res.json()).data as SerialUnit;
    },
    onSuccess: (unit) => {
      invalidate();
      showToast(t('serialsPage.statusChanged', { serial: unit.serial, status: statusLabel(unit.status) }), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  // Every unit with the looked-up serial (slice 2); a slice-1 response carries only `unit`.
  const lookupUnits: SerialUnit[] = lookup.data
    ? lookup.data.units ?? (lookup.data.unit ? [lookup.data.unit] : [])
    : [];
  /** A unit with no catalog product: its brand when known, else its SKU (lookup) or "no product". */
  const noProductLabel = (unit: SerialUnit, withSku = true): string =>
    unit.brand
      ? t('serialsPage.noProductBrand', { brand: unit.brand })
      : (withSku && unit.storeSku) || t('serialsPage.noProduct');

  const counts = data?.counts;
  const coverage = useMemo(() => {
    if (!counts) return undefined;
    return t('serialsPage.coverage', { inStock: counts.in_stock, sold: counts.sold, returned: counts.returned });
  }, [counts, t]);

  const pageLabel = data
    ? data.total === null
      ? t('serialsPage.pageSearch', { shown: data.units.length })
      : t('serialsPage.pageOf', { shown: data.units.length, total: data.total })
    : '';

  const templateProps = {
    fileName: SERIAL_RECEIVING_TEMPLATE.fileName,
    columns: [
      { name: 'serial', required: true, description: t('serialsPage.template.serial'), example: SERIAL_RECEIVING_TEMPLATE.rows[0][0] },
      { name: 'sku', description: t('serialsPage.template.sku'), example: SERIAL_RECEIVING_TEMPLATE.rows[0][1] },
      { name: 'upc', description: t('serialsPage.template.upc'), example: SERIAL_RECEIVING_TEMPLATE.rows[0][2] },
      { name: 'product_id', description: t('serialsPage.template.productId') },
      { name: 'received_at', description: t('serialsPage.template.receivedAt'), example: SERIAL_RECEIVING_TEMPLATE.rows[0][4] },
      { name: 'brand', description: t('serialsPage.template.brand'), example: SERIAL_RECEIVING_TEMPLATE.rows[1][5] },
    ],
    exampleRows: SERIAL_RECEIVING_TEMPLATE.rows.map((r) => [...r]),
  };

  if (!flagsLoading && !enabled) {
    return (
      <PageContainer>
        <EmptyState title={t('serialsPage.off.title')} description={t('serialsPage.off.description')} />
      </PageContainer>
    );
  }

  const emptyTitle =
    search.trim()
      ? t('serialsPage.empty.search', { search: search.trim() })
      : statusFilter === 'all'
        ? t('serialsPage.empty.all')
        : t('serialsPage.empty.status', { status: statusLabel(statusFilter).toLowerCase() });

  return (
    <PageContainer>
      <PageHeader
        title={<TrialFeatureTitle title={t('serialsPage.title')} feature="rebateTracking" />}
        description={t('serialsPage.description')}
        actions={
          canWrite ? (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<UploadFileOutlined />} onClick={() => { setUploadResult(null); setUploadOpen(true); }}>
                {t('serialsPage.upload.button')}
              </Button>
              <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setAddOpen(true)}>
                {t('serialsPage.add.button')}
              </Button>
            </Stack>
          ) : undefined
        }
      />

      <Stack spacing={3}>
        <SectionCard title={t('serialsPage.lookup.title')} subtitle={t('serialsPage.lookup.subtitle')}>
          <Box
            component="form"
            onSubmit={(e) => { e.preventDefault(); setLookupSerial(lookupInput.trim()); }}
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <TextField
              size="small"
              label={t('serialsPage.lookup.label')}
              value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)}
              inputProps={{ spellCheck: false }}
              sx={{ minWidth: 260, flex: '1 1 260px' }}
            />
            <Button type="submit" variant="contained" startIcon={<SearchOutlined />} disabled={!lookupInput.trim()}>
              {t('serialsPage.lookup.button')}
            </Button>
          </Box>
          {lookupSerial && (
            <Box sx={{ mt: 2 }}>
              {lookup.isLoading ? (
                <TableSkeleton rows={1} />
              ) : lookup.error ? (
                <InlineMessage severity="error">{(lookup.error as Error).message}</InlineMessage>
              ) : lookup.data && lookupUnits.length === 0 && lookup.data.soldLines.length === 0 ? (
                <EmptyState size="compact" title={t('serialsPage.lookup.notFound', { serial: lookup.data.serial })} description={t('serialsPage.lookup.notFoundHint')} />
              ) : lookup.data ? (
                <Stack spacing={1.5}>
                  {lookupUnits.length > 1 && (
                    <InlineMessage severity="info">{t('serialsPage.lookup.severalUnits', { count: lookupUnits.length })}</InlineMessage>
                  )}
                  {lookupUnits.map((unit) => (
                    <Stack key={unit.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" sx={{ fontFamily: tokens.font.mono }}>{unit.serial}</Typography>
                      <StatusChip tone={STATUS_TONE[unit.status]} label={statusLabel(unit.status)} dot />
                      {unit.product ? (
                        <Link component={RouterLink} to={productLink(unit.product.id)} underline="hover" variant="body2">
                          {[unit.product.brand, unit.product.name].filter(Boolean).join(' ')}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{noProductLabel(unit)}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {t('serialsPage.lookup.received', { date: day(unit.receivedAt) })}
                      </Typography>
                    </Stack>
                  ))}
                  {lookup.data.soldLines.map((line) => {
                    // One claim per rebate program the unit earned on this line (MOT-420).
                    const claims = (line.serialClaims ?? []).filter((c) => c.serial === lookup.data!.serial);
                    // A refunded or voided sale gave the unit back, unless a claim held it (MOT-419).
                    const refunded = line.financialStatus === 'refunded' || line.financialStatus === 'voided';
                    const held = (line.serialRefundHolds ?? []).some((h) => h.serial === lookup.data!.serial);
                    return (
                      <Stack key={line.orderItemId} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2">
                          {t('serialsPage.lookup.soldLine', {
                            order: line.orderNumber,
                            date: day(line.orderedAt),
                            product: line.productName || line.sku,
                            platform: line.platform || '',
                          })}
                        </Typography>
                        {refunded && (
                          <StatusChip size="small" tone="neutral" label={t(`serialsPage.lookup.${line.financialStatus}`)} />
                        )}
                        {held && (
                          <Tooltip title={t('serialsPage.lookup.refundHeldHint')}>
                            <span><StatusChip size="small" tone="caution" label={t('serialsPage.lookup.refundHeld')} /></span>
                          </Tooltip>
                        )}
                        {claims.map((claim) => (
                          <StatusChip
                            key={claim.programKey}
                            size="small"
                            tone={claim.status === 'paid' ? 'positive' : 'info'}
                            label={t('serialsPage.lookup.claim', { status: t(`store.rebateSerials.claim.${claim.status}`).toLowerCase() })}
                          />
                        ))}
                      </Stack>
                    );
                  })}
                  {lookupUnits.length > 0 && lookup.data.soldLines.length === 0 && lookupUnits.every((u) => u.status === 'in_stock') && (
                    <Typography variant="body2" color="text.secondary">{t('serialsPage.lookup.notSold')}</Typography>
                  )}
                </Stack>
              ) : null}
            </Box>
          )}
        </SectionCard>

        <SectionCard dense title={t('serialsPage.list.title')} subtitle={coverage}>
          <DataTableShell
            toolbar={
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={statusFilter}
                  onChange={(_, v: StatusFilter | null) => { if (v) { setStatusFilter(v); resetPages(); } }}
                >
                  <ToggleButton value="all">{t('serialsPage.filter.all')}{counts ? ` (${counts.in_stock + counts.sold + counts.returned})` : ''}</ToggleButton>
                  {(['in_stock', 'sold', 'returned'] as UnitStatus[]).map((s) => (
                    <ToggleButton key={s} value={s}>{statusLabel(s)}{counts ? ` (${counts[s]})` : ''}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  placeholder={t('serialsPage.filter.search')}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); resetPages(); }}
                  inputProps={{ spellCheck: false }}
                  sx={{ minWidth: 240 }}
                />
              </Stack>
            }
          >
            {error && <InlineMessage severity="error">{(error as Error).message}</InlineMessage>}
            {isLoading ? (
              <TableSkeleton rows={6} />
            ) : !data || data.units.length === 0 ? (
              <EmptyState size="compact" title={emptyTitle} description={canWrite ? t('serialsPage.empty.hintWrite') : t('serialsPage.empty.hint')} />
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('serialsPage.columns.serial')}</TableCell>
                      <TableCell>{t('serialsPage.columns.product')}</TableCell>
                      <TableCell>{t('serialsPage.columns.status')}</TableCell>
                      <TableCell>{t('serialsPage.columns.received')}</TableCell>
                      <TableCell>{t('serialsPage.columns.sold')}</TableCell>
                      <TableCell>{t('serialsPage.columns.source')}</TableCell>
                      {canWrite && <TableCell align="right">{t('serialsPage.columns.actions')}</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.units.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ fontFamily: tokens.font.mono }}>{u.serial}</TableCell>
                        <TableCell>
                          {u.product ? (
                            <Link component={RouterLink} to={productLink(u.product.id)} underline="hover">
                              {[u.product.brand, u.product.name].filter(Boolean).join(' ')}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">{noProductLabel(u, false)}</Typography>
                          )}
                          {u.storeSku && <Typography variant="caption" color="text.secondary" display="block">{u.storeSku}</Typography>}
                        </TableCell>
                        <TableCell><StatusChip tone={STATUS_TONE[u.status]} label={statusLabel(u.status)} dot /></TableCell>
                        <TableCell>{day(u.receivedAt)}</TableCell>
                        <TableCell>
                          {u.status === 'sold' ? (
                            <Tooltip title={u.soldOrderItemId || ''}>
                              <span>{day(u.soldAt)}</span>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{sourceLabel(u.source)}</TableCell>
                        {canWrite && (
                          <TableCell align="right">
                            {u.status === 'sold' && (
                              <Button size="small" onClick={() => statusMutation.mutate({ id: u.id, status: 'returned' })} disabled={statusMutation.isPending}>
                                {t('serialsPage.actions.markReturned')}
                              </Button>
                            )}
                            {u.status === 'returned' && (
                              <Button size="small" onClick={() => statusMutation.mutate({ id: u.id, status: 'in_stock' })} disabled={statusMutation.isPending}>
                                {t('serialsPage.actions.backInStock')}
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">{pageLabel}</Typography>
                  {cursors.length > 0 && (
                    <Button size="small" onClick={() => setCursors((c) => c.slice(0, -1))}>{t('serialsPage.previousPage')}</Button>
                  )}
                  {data.nextCursor && (
                    <Button size="small" onClick={() => setCursors((c) => [...c, data.nextCursor!])}>{t('serialsPage.nextPage')}</Button>
                  )}
                </Stack>
              </>
            )}
          </DataTableShell>
        </SectionCard>
      </Stack>

      <FormDialog
        open={uploadOpen}
        title={t('serialsPage.upload.title')}
        onClose={() => { setUploadOpen(false); setUploadFile(null); }}
        onSubmit={() => uploadFile && uploadMutation.mutate(uploadFile)}
        submitLabel={t('serialsPage.upload.submit')}
        cancelLabel={t('common.cancel')}
        submitDisabled={!uploadFile}
        loading={uploadMutation.isPending}
        maxWidth="sm"
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">{t('serialsPage.upload.help')}</Typography>
          <CsvTemplateHint {...templateProps} compact />
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadResult(null); }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" onClick={() => fileInput.current?.click()}>{t('serialsPage.upload.choose')}</Button>
            <Typography variant="body2" color="text.secondary">{uploadFile ? uploadFile.name : t('serialsPage.upload.noFile')}</Typography>
          </Stack>
          {uploadResult && (
            <InlineMessage severity={uploadResult.errors.length > 0 ? 'warning' : 'success'}>
              {t('serialsPage.upload.result', {
                total: uploadResult.totalRows, created: uploadResult.created, updated: uploadResult.updated,
                skipped: uploadResult.skipped, unmatched: uploadResult.unmatched,
              })}
              {uploadResult.errors.slice(0, 5).map((e) => (
                <Typography key={`${e.row}-${e.message}`} variant="caption" display="block">
                  {t('serialsPage.upload.rowError', { row: e.row, message: e.message })}
                </Typography>
              ))}
            </InlineMessage>
          )}
        </Stack>
      </FormDialog>

      <FormDialog
        open={addOpen}
        title={t('serialsPage.add.title')}
        onClose={() => setAddOpen(false)}
        onSubmit={() => addMutation.mutate()}
        submitLabel={t('serialsPage.add.submit')}
        cancelLabel={t('common.cancel')}
        submitDisabled={!addSerial.trim()}
        loading={addMutation.isPending}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('serialsPage.add.serial')}
            value={addSerial}
            onChange={(e) => setAddSerial(e.target.value)}
            inputProps={{ spellCheck: false, maxLength: 64 }}
            autoFocus
            fullWidth
          />
          <TextField
            label={t('serialsPage.add.sku')}
            helperText={t('serialsPage.add.skuHelp')}
            value={addSku}
            onChange={(e) => setAddSku(e.target.value)}
            inputProps={{ spellCheck: false }}
            fullWidth
          />
          <BrandSelect
            label={t('serialsPage.add.brand')}
            helperText={t('serialsPage.add.brandHelp')}
            value={addBrand}
            onChange={setAddBrand}
          />
        </Stack>
      </FormDialog>
    </PageContainer>
  );
};

export default StoreSerialsPage;
